---
title: "How billing and users work"
description: "What you pay for, how seats are counted, what the free plan includes, and what happens when you add or remove people."
publishedAt: "2026-08-14"
updatedAt: "2026-10-25"
slug: "how-billing-and-users-work"
---

# How billing and users work

NerdCRM is **$4 per user, per month** — one price, no per-feature tiers. One optional add-on, [AI Call Analysis](/features/ai-call-analysis), is $5 per user per month on top.

## The free plan

Free for up to 5 users, no credit card. It includes the whole CRM: pipelines, deals, hot deals, contacts, companies, activities, notes, products, payments, reports, CSV import/export and API access.

The three paid features — call recording, WhatsApp and cold email — come with a one-time allowance to try them: 100 recordings, 100 WhatsApp messages and 300 outreach emails. See [your free allowance](/help/free-allowance-and-upgrading).

## The paid plan

Subscribing bills **every active user from the first one**. A three-person team pays $12 a month; a ten-person team pays $40. That's what unlocks the paid features, with no per-feature limits.

Billing runs monthly through Paddle. You're charged for the active user count, and adding someone mid-cycle tops the subscription up automatically rather than blocking you.

> If you subscribed before August 2026, your organization keeps its original pricing, where the first 5 users were free and you paid only for users beyond that. Nothing about your bill changed.

## AI Call Analysis (optional add-on)

AI Call Analysis transcribes every recorded call, scores it and gives each lead a recommendation. It's $5 per user per month, billed per active user, and includes 300 analysed calls per user per month, shared across the team.

- Extra calls are $3 per 100, only up to a monthly limit the owner sets (**Extra calls cap per month**).
- Paid plans without the add-on get a taster of 20 analysed calls per user per month.
- Add or remove it any time with the switch on the **AI Call Analysis add-on** card in **Settings → Billing & seats**. A confirmation shows the price before you pay: adding it charges the prorated amount for the rest of the month, and removing it credits the unused part.

## Where it all lives

The organization **owner** manages billing in **Settings → Billing & seats**:

- **Plan** shows your status and what a person costs, and **Next invoice** is an estimate of your next bill before tax (seats, the AI add-on and any extra calls). **Update payment method** opens Paddle.
- **Subscribe** (on the free plan) shows the monthly total before tax before you click **Subscribe now**, with a checkbox to include AI Call Analysis.
- **Seats** says what the next person adds to your bill for your plan, and **Manage team** opens **Team & roles**.
- **Cancel subscription** is at the bottom of the page.

People are managed in **Settings → Team & roles**.

## Adding and removing people

- **Adding a user on a paid plan** always works. The seat is added to your subscription automatically — you're never blocked mid-task to go and click something in billing.
- **Adding a user on the free plan** works until you reach 5. After that **Team & roles** says the free plan limit is reached and offers **Subscribe now**.
- **Removing a user** frees the seat and your next invoice reflects the lower count.

A person can belong to more than one organization. Seats are counted per organization, so someone who works in two of your orgs occupies a seat in each.

## If a payment fails

Nothing is switched off immediately. The subscription moves to a grace period, we email the owner, and Paddle retries. You keep full access — including the paid features — while that plays out, and **Settings → Billing & seats** shows a warning with **Update payment method** beside it.

If it's never resolved, the organization returns to the free plan. At that point, if you have more than 5 users, **Settings → Billing & seats** shows **Choose who keeps access**, where the owner picks which 5 keep access.

## Cancelling

Click **Cancel subscription** at the bottom of **Settings → Billing & seats**. A confirmation tells you the day your plan ends: you keep everything until the end of the period you've paid for, and you can click **Resume subscription** any time before then. After that the organization returns to the free plan, and if more than 5 people are active the owner chooses who keeps access:

- All your data stays — nothing is deleted.
- The paid features pause. Recordings and conversations remain, you just can't add new ones.
- Your one-time free allowance doesn't come back, so cancelling and resubscribing won't produce a second trial.

## Who can do what

Only the organization **owner** can open Settings → Billing & seats, subscribe, change the add-on or cancel. Admins can see when a feature is locked, and the lock tells them to ask the owner, but they can't make payment decisions.
